/**
 * MFE Routing Utilities
 *
 * Utilities for handling MFE rollout routing between legacy and MFE versions.
 * Supports URL overrides, session pins, and cross-domain redirects.
 */

import { isTestEnv } from './services/api/testEnv';
import { logErrorCtx } from './app/logging';

/**
 * Redirect attempt tracking for infinite loop detection
 */
interface RedirectAttempt {
  timestamp: number;
  from: string;
  to: string;
}

/**
 * Constants for infinite loop detection
 */
const REDIRECT_TRACKING_KEY = 'mfe_redirect_tracking';
const LOOP_THRESHOLD = 3;
const LOOP_WINDOW_MS = 30000; // 30 seconds

/**
 * Get a cookie value by name
 * @param name - Cookie name
 * @returns Cookie value or undefined if not found
 */
export function getCookie(name: string): string | undefined {
  return document.cookie
    .split('; ')
    .map((v) => v.split('='))
    .find(([k]) => k === name)?.[1];
}

/**
 * Set a session-only cookie (expires when browser closes)
 * @param name - Cookie name
 * @param value - Cookie value
 */
export function setSessionCookie(name: string, value: string): void {
  const isHttps = window.location.protocol === 'https:';
  const secureFlag = isHttps ? '; Secure' : '';
  document.cookie = `${name}=${value}; Path=/${secureFlag}; SameSite=Lax`;
}

/**
 * Process ?mfe=on/off query parameter for session-based overrides
 * Reads the 'mfe' query parameter and sets a session cookie if present.
 * Removes the query parameter from the URL to keep it clean.
 */
export function processSessionPinFromQuery(): void {
  const url = new URL(window.location.href);
  const mfeParam = url.searchParams.get('mfe');

  if (mfeParam === 'on' || mfeParam === 'off') {
    setSessionCookie('mfe_session_pin', mfeParam);
    url.searchParams.delete('mfe');
    history.replaceState(null, '', url.toString());
  }
}

/**
 * Track a redirect attempt for infinite loop detection
 * Stores attempt in localStorage with timestamp
 * @param from - Source URL
 * @param to - Target URL
 */
function trackRedirectAttempt(from: string, to: string): void {
  try {
    const history = getRedirectHistory();
    const attempt: RedirectAttempt = {
      timestamp: Date.now(),
      from,
      to,
    };

    history.push(attempt);

    // Keep only recent attempts (within LOOP_WINDOW_MS)
    const cutoff = Date.now() - LOOP_WINDOW_MS;
    const recentAttempts = history.filter((a) => a.timestamp > cutoff);

    localStorage.setItem(REDIRECT_TRACKING_KEY, JSON.stringify(recentAttempts));
    console.log('[Legacy] Redirect attempt tracked:', {
      attempt,
      totalRecentAttempts: recentAttempts.length,
    });
  } catch (error) {
    console.error('[Legacy] Failed to track redirect attempt:', error);
  }
}

/**
 * Get redirect history from localStorage
 * @returns Array of recent redirect attempts
 */
function getRedirectHistory(): RedirectAttempt[] {
  try {
    const data = localStorage.getItem(REDIRECT_TRACKING_KEY);
    if (!data) return [];

    const history = JSON.parse(data) as RedirectAttempt[];
    const cutoff = Date.now() - LOOP_WINDOW_MS;

    // Filter out stale attempts
    return history.filter((a) => a.timestamp > cutoff);
  } catch (error) {
    console.error('[Legacy] Failed to read redirect history:', error);
    return [];
  }
}

/**
 * Check if user is in an infinite redirect loop
 * @returns true if LOOP_THRESHOLD redirects occurred within LOOP_WINDOW_MS
 */
function isInfiniteLoop(): boolean {
  const history = getRedirectHistory();
  const isLoop = history.length >= LOOP_THRESHOLD;

  if (isLoop) {
    console.error('[Legacy] INFINITE REDIRECT LOOP DETECTED', {
      attempts: history.length,
      threshold: LOOP_THRESHOLD,
      windowMs: LOOP_WINDOW_MS,
      history,
    });
  }

  return isLoop;
}

/**
 * Clear redirect tracking from localStorage
 */
function clearRedirectTracking(): void {
  try {
    localStorage.removeItem(REDIRECT_TRACKING_KEY);
    console.log('[Legacy] Redirect tracking cleared');
  } catch (error) {
    console.error('[Legacy] Failed to clear redirect tracking:', error);
  }
}

/**
 * Clear all localStorage and sessionStorage except essential items
 * Preserves: testEnv (sandbox mode)
 */
function clearAllStorageExceptEssentials(): void {
  try {
    // Preserve essential values
    const testEnvValue = localStorage.getItem('testEnv');

    // Clear all localStorage
    localStorage.clear();

    // Restore essential values
    if (testEnvValue) {
      localStorage.setItem('testEnv', testEnvValue);
    }

    // Clear all sessionStorage
    sessionStorage.clear();

    console.log('[Legacy] Storage cleared (except testEnv)');
  } catch (error) {
    console.error('[Legacy] Failed to clear storage:', error);
  }
}

/**
 * Redirect to a different hostname while preserving path, query, and hash
 * @param hostname - Target hostname (e.g., 'fishbowlonline.com')
 */
export function redirectToHost(hostname: string): void {
  const { protocol, pathname, search, hash } = window.location;
  window.location.replace(
    `${protocol}//${hostname}${pathname}${search}${hash}`
  );
}

/**
 * Get environment prefix from hostname (dev., qa., stage., or empty for prod)
 */
function getEnvironmentPrefix(hostname: string): string {
  const match = hostname.match(/^(dev|qa|stage)\./);
  return match ? `${match[1]}.` : '';
}

/**
 * Redirect to MFE version while preserving path, query, hash, environment, and sandbox mode
 * Adds /drive prefix to the path as required by MFE architecture
 * Includes infinite loop detection and prevention
 */
export function redirectToMFE(): void {
  const { protocol, hostname, pathname, search, hash } = window.location;
  const currentUrl = window.location.href;

  console.log('[Legacy] redirectToMFE: Starting redirect', {
    currentLocation: currentUrl,
    hostname,
    pathname,
  });

  // Preserve environment prefix (dev., qa., stage., or none for prod)
  const envPrefix = getEnvironmentPrefix(hostname);
  console.log(
    '[Legacy] redirectToMFE: Environment prefix:',
    envPrefix || 'NONE (prod)'
  );

  // Add /drive prefix for MFE routing
  const mfePath = `/drive${pathname}`;

  // Build search params
  const searchParams = new URLSearchParams(search);

  // Preserve sandbox mode if enabled
  const wasSandbox = isTestEnv();
  if (wasSandbox) {
    searchParams.set('sandbox', 'true');
  }

  // Preserve session pin if set
  const sessionPin = getCookie('mfe_session_pin');
  if (sessionPin === 'on' || sessionPin === 'off') {
    searchParams.set('mfe', sessionPin);
    console.log(
      '[Legacy] redirectToMFE: Preserving session pin in URL:',
      sessionPin
    );
  }

  const finalSearch = searchParams.toString()
    ? `?${searchParams.toString()}`
    : '';

  const targetUrl = `${protocol}//${envPrefix}fishbowlinventory.app${mfePath}${finalSearch}${hash}`;

  // Track this redirect attempt BEFORE checking for loop
  // (Must track before clearing storage so history accumulates)
  trackRedirectAttempt(currentUrl, targetUrl);

  // Check for infinite loop AFTER tracking
  if (isInfiniteLoop()) {
    console.error(
      '[Legacy] redirectToMFE: Infinite loop detected, aborting redirect'
    );

    // Set session pin to OFF to prevent further redirects
    setSessionCookie('mfe_session_pin', 'off');
    console.log('[Legacy] redirectToMFE: Set session pin to OFF to break loop');

    // Clear all state except essentials
    clearAllStorageExceptEssentials();
    clearRedirectTracking();

    // Log catastrophic event to DataDog
    const redirectHistory = getRedirectHistory();
    logErrorCtx('MFE Infinite Redirect Loop Detected', {
      title: 'MFE Infinite Redirect Loop',
      description: `User caught in redirect loop between legacy and MFE. Forced to legacy.`,
      component: 'mfeRouting',
      route: pathname,
      redirectHistory: JSON.stringify(redirectHistory),
      currentUrl,
      targetUrl,
      redirectCount: redirectHistory.length,
      sessionPin,
      error: new Error('Infinite redirect loop detected'),
    } as any);

    // Stay on legacy (do not redirect)
    alert(
      'We detected an issue with MFE routing. You have been kept on the legacy site. Please contact support if this persists.'
    );
    return;
  }

  // Clear all storage except essentials before redirecting
  clearAllStorageExceptEssentials();

  // Log successful redirect to DataDog
  import('./app/logging')
    .then(({ logInfoCtx }) => {
      logInfoCtx('MFE Redirect Success: Legacy → MFE', {
        title: 'MFE Redirect Success',
        description: 'User successfully redirected from legacy to MFE',
        component: 'mfeRouting',
        route: pathname,
        currentUrl,
        targetUrl,
        sessionPin: sessionPin || 'none',
        sandboxMode: wasSandbox,
      } as any);
    })
    .catch(() => {
      // Ignore logging errors, don't block redirect
    });

  console.log('[Legacy] redirectToMFE: Redirecting to:', targetUrl, {
    sandboxMode: wasSandbox,
    sessionPin,
  });

  window.location.replace(targetUrl);
}

/**
 * Check MFE routing after Auth0 callback completes
 * Called from Auth0Handler after user is authenticated
 * @param user - Auth0 user object from useAuth0 hook
 * Returns true if redirecting to MFE, false if staying on legacy
 */
export async function checkMfeRoutingAfterAuth(user: any): Promise<boolean> {
  try {
    console.log(
      '[Legacy] MFE routing: checkMfeRoutingAfterAuth called with user:',
      user ? 'PRESENT' : 'NULL'
    );

    // Check session pin override
    const sessionPin = getCookie('mfe_session_pin');
    console.log(
      '[Legacy] MFE routing: Session pin cookie:',
      sessionPin || 'NOT SET'
    );

    if (sessionPin === 'off') {
      console.log('[Legacy] MFE routing: Session pin = off, staying on legacy');
      return false;
    }

    if (sessionPin === 'on') {
      console.log('[Legacy] MFE routing: Session pin = on, redirecting to MFE');
      redirectToMFE();
      return true;
    }

    // Validate user was provided
    if (!user) {
      console.error(
        '[Legacy] MFE routing: No user info provided, staying on legacy'
      );
      return false;
    }

    console.log('[Legacy] MFE routing: User object received:', {
      sub: user.sub,
      email: user.email,
      hasOrgId: !!(user.org_id || user.organizationId),
      hasRoles: !!user.roles,
      rolesArray: user.roles,
    });

    // Dynamic import to avoid circular dependencies
    const LDClient = await import('launchdarkly-js-client-sdk');

    const userId = user.sub || 'anonymous';
    const orgId = (user as any).org_id || (user as any).organizationId || '';
    const roles = (user as any).roles || [];
    const isDev = roles.includes('dev');

    // Initialize LaunchDarkly
    const ldClientSideId = process.env.REACT_APP_LD_CLIENT_SIDE_ID;
    if (!ldClientSideId) {
      console.warn('[Legacy] MFE routing: LD client ID not configured');
      return false;
    }

    const ldContext: any = {
      kind: 'user',
      key: userId,
      orgId,
      roles,
      custom: {
        orgId,
        roles: roles.join(','),
      },
    };

    console.log('[Legacy] MFE routing: Initializing LD with context', {
      ldClientSideId: ldClientSideId?.substring(0, 10) + '...',
      userId,
      orgId,
      roles,
      isDev,
      fullContext: ldContext,
    });

    const ldClient = LDClient.initialize(ldClientSideId, ldContext);
    console.log(
      '[Legacy] MFE routing: LD client created, waiting for initialization...'
    );

    await ldClient.waitForInitialization();
    console.log('[Legacy] MFE routing: LD initialization complete');

    const mfeEnabled = ldClient.variation('mfe-enabled', false);
    console.log(
      '[Legacy] MFE routing: LD variation result for "mfe-enabled":',
      mfeEnabled
    );

    console.log('[Legacy] MFE routing: Flag evaluation summary', {
      mfeEnabled,
      isDev,
      willRedirect: mfeEnabled || isDev,
      redirectReason: mfeEnabled
        ? 'mfe-enabled flag is true'
        : isDev
        ? 'user has dev role'
        : 'staying on legacy',
    });

    ldClient.close();

    if (mfeEnabled || isDev) {
      console.log('[Legacy] MFE routing: Redirecting to MFE');
      redirectToMFE();
      return true;
    }

    console.log('[Legacy] MFE routing: Staying on legacy');
    return false;
  } catch (error) {
    console.error('[Legacy] MFE routing check failed after auth', error);
    return false;
  }
}
